/**
 * Integration Types
 *
 * This module contains type definitions for the API integration layer,
 * including Axios integration, authentication integration, and performance
 * monitoring integration configurations and state types.
 *
 * @module types/api/integration
 */

import {
  type DeduplicationOptions,
  type IRequestCoordinator,
  type IAxiosIntegration,
  type RequestContext,
  DEFAULT_DEDUPLICATION_OPTIONS,
} from './request.types';
import { DEFAULT_RETRY_CONFIG, type RetryConfig } from './retry.types';

import type { CircuitBreakerConfig } from './circuit-breaker.types';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

// =============================================================================
// AXIOS INTEGRATION TYPES
// =============================================================================

/**
 * Extended Axios instance that stores the original request method
 * before coordinator integration overrides it.
 *
 * This interface is used to prevent infinite recursion when the coordinator
 * needs to make actual HTTP requests internally. By storing the original
 * request method, we can bypass the coordinator override when needed.
 *
 * @example
 * ```typescript
 * const client = axios.create() as AxiosInstanceWithOriginal;
 *
 * // Store original before override
 * client.originalRequest = client.request.bind(client);
 *
 * // Override request method
 * client.request = async (config) => {
 *   if (config.skipCoordinator) {
 *     return client.originalRequest!(config);
 *   }
 *   return coordinator.execute(config);
 * };
 * ```
 */
export interface AxiosInstanceWithOriginal extends AxiosInstance {
  /** Original axios.request method, stored to prevent infinite recursion */
  originalRequest?: AxiosInstance['request'];
}

/**
 * Base coordinator configuration options.
 *
 * @example
 * ```typescript
 * const config: CoordinatorConfig = {
 *   retry: DEFAULT_RETRY_CONFIG,
 *   deduplication: DEFAULT_DEDUPLICATION_OPTIONS,
 *   enablePerformanceMonitoring: true,
 *   enableCircuitBreaker: true,
 *   enableNotifications: true,
 *   enableLogging: false,
 *   memoryCleanupInterval: 60000,
 * };
 * ```
 */
export interface CoordinatorConfig {
  /** Retry configuration for failed requests */
  retry: RetryConfig;
  /** Deduplication configuration for duplicate requests */
  deduplication: DeduplicationOptions;
  /** Whether to enable performance monitoring */
  enablePerformanceMonitoring: boolean;
  /** Whether to enable circuit breaker protection */
  enableCircuitBreaker: boolean;
  /**
   * Optional circuit breaker thresholds (failureThreshold, timeout, etc.).
   * When omitted, DEFAULT_CIRCUIT_BREAKER_CONFIG is used. The breaker's
   * `enabled` flag is driven by `enableCircuitBreaker`.
   */
  circuitBreaker?: Partial<CircuitBreakerConfig>;
  /** Whether to enable notification system */
  enableNotifications: boolean;
  /** Whether to enable logging */
  enableLogging: boolean;
  /** Interval for memory cleanup in milliseconds */
  memoryCleanupInterval: number;
}

/**
 * Configuration options for Axios integration with the request coordinator.
 *
 * Extends the base coordinator configuration with Axios-specific options
 * for legacy mode support and debugging.
 *
 * @example
 * ```typescript
 * const config: AxiosIntegrationConfig = {
 *   retry: DEFAULT_RETRY_CONFIG,
 *   deduplication: DEFAULT_DEDUPLICATION_OPTIONS,
 *   enablePerformanceMonitoring: true,
 *   enableCircuitBreaker: true,
 *   enableNotifications: true,
 *   enableLogging: false,
 *   memoryCleanupInterval: 60000,
 *   integration: {
 *     preserveAuthInterceptor: true,
 *     preservePerformanceMonitoring: true,
 *     preserveNotificationSystem: true,
 *     enableLegacyCompatibility: false,
 *   },
 *   enableLegacyMode: false,
 *   debug: true,
 * };
 * ```
 */
export interface AxiosIntegrationConfig extends CoordinatorConfig {
  /** Integration layer configuration */
  integration: IntegrationConfig;
  /** Enable legacy mode for backward compatibility */
  enableLegacyMode?: boolean;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Integration layer configuration options.
 *
 * Controls which existing behaviors are preserved when integrating
 * the new coordinator system.
 *
 * @example
 * ```typescript
 * const integrationConfig: IntegrationConfig = {
 *   preserveAuthInterceptor: true,
 *   preservePerformanceMonitoring: true,
 *   preserveNotificationSystem: true,
 *   enableLegacyCompatibility: false,
 * };
 * ```
 */
export interface IntegrationConfig {
  /** Whether to preserve existing auth interceptor behavior */
  preserveAuthInterceptor: boolean;
  /** Whether to preserve existing performance monitoring */
  preservePerformanceMonitoring: boolean;
  /** Whether to preserve existing notification system */
  preserveNotificationSystem: boolean;
  /** Whether to enable legacy compatibility mode */
  enableLegacyCompatibility: boolean;
}

/**
 * Context maintained by the Axios integration during its lifecycle.
 *
 * Contains references to the coordinator, configuration, client instance,
 * active requests, and integration metrics.
 *
 * @example
 * ```typescript
 * const context: AxiosIntegrationContext = {
 *   coordinator: requestCoordinator,
 *   config: axiosIntegrationConfig,
 *   client: axiosInstance,
 *   activeRequests: new Map(),
 *   interceptorIds: { request: [1, 2], response: [3] },
 *   isSetup: true,
 *   metrics: {
 *     totalRequests: 100,
 *     successfulRequests: 95,
 *     failedRequests: 5,
 *     retriedRequests: 10,
 *     averageResponseTime: 250,
 *   },
 * };
 * ```
 */
export interface AxiosIntegrationContext {
  /** Request coordinator instance */
  coordinator: IRequestCoordinator;
  /** Integration configuration */
  config: AxiosIntegrationConfig;
  /** Axios client instance */
  client: AxiosInstance;
  /**
   * Map of active requests by ID.
   *
   * @deprecated Request lifecycle is now owned by the coordinator's request
   * tracker. Retained as optional for backward compatibility; the integration
   * layer no longer populates it. Use `coordinator.getActiveRequests()`.
   */
  activeRequests?: Map<string, RequestContext>;
  /** IDs of registered interceptors */
  interceptorIds: {
    request: number[];
    response: number[];
  };
  /** Whether integration has been set up */
  isSetup: boolean;
  /**
   * Integration metrics.
   *
   * @deprecated The coordinator is now the single source of truth for request
   * metrics. Retained as optional for backward compatibility; the integration
   * layer no longer populates it. Use `coordinator.getMetrics().summary`.
   */
  metrics?: AxiosIntegrationMetrics;
}

/**
 * Metrics collected by the Axios integration.
 *
 * @example
 * ```typescript
 * const metrics: AxiosIntegrationMetrics = {
 *   totalRequests: 100,
 *   successfulRequests: 95,
 *   failedRequests: 5,
 *   retriedRequests: 10,
 *   averageResponseTime: 250,
 * };
 * ```
 */
export interface AxiosIntegrationMetrics {
  /** Total number of requests processed */
  totalRequests: number;
  /** Number of successful requests */
  successfulRequests: number;
  /** Number of failed requests */
  failedRequests: number;
  /** Number of requests that were retried */
  retriedRequests: number;
  /** Average response time in milliseconds */
  averageResponseTime: number;
}

// =============================================================================
// AUTHENTICATION INTEGRATION TYPES
// =============================================================================

/**
 * Interface representing an authentication error.
 *
 * This interface is used for type checking authentication-related errors
 * that may occur during API requests.
 *
 * @example
 * ```typescript
 * const authError: AuthErrorInterface = {
 *   name: 'AuthError',
 *   message: 'Token expired',
 *   status: 401,
 *   response: {
 *     status: 401,
 *     data: {
 *       message: 'Token has expired',
 *       error: 'UNAUTHORIZED',
 *     },
 *   },
 * };
 * ```
 */
export interface AuthErrorInterface {
  /** Error name */
  name: string;
  /** Error message */
  message: string;
  /** HTTP status code */
  status: number;
  /** Response object if available */
  response?: {
    status: number;
    data: {
      message?: string;
      error?: string;
      errors?: Record<string, string[]>;
    };
  };
  /** Original request configuration */
  config?: AxiosRequestConfig;
}

/**
 * Configuration options for authentication integration.
 *
 * Controls whether 401 errors are recovered from, endpoints to skip,
 * how the bearer is read off a request, and the callback for auth errors.
 *
 * @example
 * ```typescript
 * const authConfig: AuthIntegrationConfig = {
 *   enableAutoRefresh: true,
 *   skipAuthEndpoints: ['/auth/login', '/auth/logout'],
 *   authHeader: 'Authorization',
 *   tokenPrefix: 'Bearer ',
 *   onAuthError: (error) => console.error('Auth error', error),
 * };
 * ```
 */
export interface AuthIntegrationConfig {
  /**
   * Whether to recover from 401 errors. A 401 is never answered with a token
   * refresh (the refused bearer is the one that would sign it): a request whose
   * bearer was rotated while it was in flight is replayed, and one refused with
   * the current bearer ends the session.
   */
  enableAutoRefresh: boolean;
  /** Endpoints to skip authentication handling */
  skipAuthEndpoints: string[];
  /** Header name for authorization */
  authHeader: string;
  /** Prefix for the token in the header */
  tokenPrefix: string;
  /** Callback when an auth error occurs */
  onAuthError?: (error: AuthErrorInterface) => void;
}

/**
 * Result of a token refresh operation.
 *
 * @example
 * ```typescript
 * const result: AuthTokenRefreshResult = {
 *   success: true,
 *   token: 'new-access-token',
 *   shouldRetry: true,
 * };
 * ```
 */
export interface AuthTokenRefreshResult {
  /** Whether the refresh was successful */
  success: boolean;
  /** New access token if successful */
  token?: string;
  /** Error if refresh failed */
  error?: AuthErrorInterface;
  /** Whether the original request should be retried */
  shouldRetry: boolean;
}

// =============================================================================
// PERFORMANCE INTEGRATION TYPES
// =============================================================================

/**
 * Configuration options for performance monitoring integration.
 *
 * Controls what metrics are collected, storage limits, and threshold callbacks.
 *
 * @example
 * ```typescript
 * const perfConfig: PerformanceIntegrationConfig = {
 *   enableDetailedMetrics: true,
 *   enableRetryTracking: true,
 *   enableCircuitBreakerMetrics: true,
 *   enableTimelineReconstruction: true,
 *   memoryEfficientStorage: true,
 *   maxStoredRequests: 1000,
 *   metricsRetentionMs: 5 * 60 * 1000,
 *   performanceThresholds: {
 *     warning: 3000,
 *     critical: 10000,
 *   },
 *   onPerformanceWarning: (metrics) => console.warn('Slow request', metrics),
 * };
 * ```
 */
export interface PerformanceIntegrationConfig {
  /** Whether to collect detailed metrics */
  enableDetailedMetrics: boolean;
  /** Whether to track retry attempts */
  enableRetryTracking: boolean;
  /** Whether to track circuit breaker events */
  enableCircuitBreakerMetrics: boolean;
  /** Whether to enable timeline reconstruction */
  enableTimelineReconstruction: boolean;
  /** Whether to use memory-efficient storage */
  memoryEfficientStorage: boolean;
  /** Maximum number of requests to store metrics for */
  maxStoredRequests: number;
  /** How long to retain metrics in milliseconds */
  metricsRetentionMs: number;
  /** Response time thresholds for warnings */
  performanceThresholds: {
    /** Warning threshold in milliseconds */
    warning: number;
    /** Critical threshold in milliseconds */
    critical: number;
  };
  /** Callback when warning threshold is exceeded */
  onPerformanceWarning?: (metrics: PerformanceMetricsBase) => void;
  /** Callback when critical threshold is exceeded */
  onPerformanceCritical?: (metrics: PerformanceMetricsBase) => void;
}

/**
 * Base performance metrics interface.
 * Extended by DetailedPerformanceMetrics for full tracking.
 */
export interface PerformanceMetricsBase {
  /** Unique identifier for the request */
  requestId: string;
  /** Start time in milliseconds */
  startTime: number;
  /** End time in milliseconds */
  endTime?: number;
  /** Duration in milliseconds */
  duration?: number;
  /** Response size in bytes */
  responseSize?: number;
  /** Number of retry attempts */
  retryCount: number;
}

/**
 * Full performance metrics interface for request tracking.
 *
 * This interface provides comprehensive timing and size metrics for API requests,
 * including network timing breakdown when available.
 *
 * @example
 * ```typescript
 * const metrics: PerformanceMetrics = {
 *   requestId: 'req_123',
 *   startTime: dayjs().valueOf(),
 *   endTime: dayjs().valueOf() + 500,
 *   duration: 500,
 *   dnsLookupTime: 10,
 *   connectTime: 50,
 *   tlsHandshakeTime: 30,
 *   firstByteTime: 100,
 *   downloadTime: 200,
 *   requestSize: 256,
 *   responseSize: 1024,
 *   retryCount: 0,
 *   totalDuration: 500,
 * };
 * ```
 */
export interface PerformanceMetrics {
  /** Unique identifier for the request */
  requestId: string;
  /** Start time in milliseconds */
  startTime: number;
  /** End time in milliseconds */
  endTime?: number;
  /** Duration in milliseconds */
  duration?: number;
  /** DNS lookup time in milliseconds */
  dnsLookupTime?: number;
  /** TCP connection time in milliseconds */
  connectTime?: number;
  /** TLS handshake time in milliseconds */
  tlsHandshakeTime?: number;
  /** Time to first byte in milliseconds */
  firstByteTime?: number;
  /** Download time in milliseconds */
  downloadTime?: number;
  /** Request size in bytes */
  requestSize?: number;
  /** Response size in bytes */
  responseSize?: number;
  /** Number of retry attempts */
  retryCount: number;
  /** Total duration including retries in milliseconds */
  totalDuration?: number;
}

/**
 * Detailed performance metrics including retry, circuit breaker, and auth events.
 *
 * @example
 * ```typescript
 * const metrics: DetailedPerformanceMetrics = {
 *   requestId: 'req_123',
 *   startTime: dayjs().valueOf(),
 *   endTime: dayjs().valueOf() + 500,
 *   duration: 500,
 *   responseSize: 1024,
 *   retryCount: 0,
 *   retryAttempts: [],
 *   circuitBreakerEvents: [],
 *   authenticationEvents: [],
 *   timeline: [
 *     { timestamp: dayjs().valueOf(), type: 'request_start' },
 *     { timestamp: dayjs().valueOf() + 500, type: 'request_end', duration: 500 },
 *   ],
 * };
 * ```
 */
export interface DetailedPerformanceMetrics extends PerformanceMetricsBase {
  /** Retry attempts with timing information */
  retryAttempts: IntegrationRetryAttemptMetrics[];
  /** Circuit breaker events that occurred */
  circuitBreakerEvents: IntegrationCircuitBreakerEvent[];
  /** Authentication events that occurred */
  authenticationEvents: IntegrationAuthEvent[];
  /** Deduplication metrics if applicable */
  deduplicationMetrics?: IntegrationDeduplicationMetrics;
  /** Timeline of events during the request */
  timeline: IntegrationTimelineEvent[];
  /** Network timing metrics if available */
  networkMetrics?: IntegrationNetworkMetrics;
}

/**
 * Metrics for a single retry attempt.
 *
 * @example
 * ```typescript
 * const retryMetrics: IntegrationRetryAttemptMetrics = {
 *   attemptNumber: 1,
 *   startTime: dayjs().valueOf(),
 *   endTime: dayjs().valueOf() + 1000,
 *   duration: 1000,
 *   delay: 500,
 *   reason: 'Network timeout',
 *   error: 'ECONNRESET',
 *   circuitBreakerState: 'closed',
 * };
 * ```
 */
export interface IntegrationRetryAttemptMetrics {
  /** Attempt number (1-based) */
  attemptNumber: number;
  /** Start time in milliseconds */
  startTime: number;
  /** End time in milliseconds */
  endTime?: number;
  /** Duration in milliseconds */
  duration?: number;
  /** Error message if attempt failed */
  error?: string;
  /** Delay before this attempt in milliseconds */
  delay: number;
  /** Reason for the retry */
  reason: string;
  /** Circuit breaker state at time of attempt */
  circuitBreakerState?: string;
}

/**
 * Circuit breaker event for performance tracking.
 *
 * @example
 * ```typescript
 * const event: IntegrationCircuitBreakerEvent = {
 *   timestamp: dayjs().valueOf(),
 *   state: 'open',
 *   endpoint: '/api/users',
 *   reason: 'Failure threshold exceeded',
 *   failureCount: 5,
 *   duration: 30000,
 * };
 * ```
 */
export interface IntegrationCircuitBreakerEvent {
  /** Timestamp of the event */
  timestamp: number;
  /** Circuit state (closed, open, half_open) */
  state: string;
  /** Affected endpoint */
  endpoint: string;
  /** Reason for state change */
  reason: string;
  /** Number of failures that triggered the event */
  failureCount: number;
  /** Duration in the state (if applicable) */
  duration?: number;
}

/**
 * Authentication event for performance tracking.
 *
 * @example
 * ```typescript
 * const event: IntegrationAuthEvent = {
 *   timestamp: dayjs().valueOf(),
 *   type: 'refresh_success',
 *   duration: 150,
 * };
 * ```
 */
export interface IntegrationAuthEvent {
  /** Timestamp of the event */
  timestamp: number;
  /** Type of auth event */
  type: 'refresh_started' | 'refresh_success' | 'refresh_failed' | 'auth_error';
  /** Duration of the auth operation */
  duration?: number;
  /** Error message if applicable */
  error?: string;
}

/**
 * Deduplication metrics for a request.
 *
 * @example
 * ```typescript
 * const dedup: IntegrationDeduplicationMetrics = {
 *   wasDeduplicated: true,
 *   cacheKey: 'GET:/api/users',
 *   subscribers: 3,
 *   cacheDuration: 5000,
 * };
 * ```
 */
export interface IntegrationDeduplicationMetrics {
  /** Whether this request was deduplicated */
  wasDeduplicated: boolean;
  /** Cache key used for deduplication */
  cacheKey?: string;
  /** Number of subscribers waiting for this request */
  subscribers: number;
  /** How long the result was cached */
  cacheDuration: number;
}

/**
 * Timeline event types for request lifecycle tracking.
 */
export type IntegrationTimelineEventType =
  | 'request_start'
  | 'request_end'
  | 'retry_start'
  | 'retry_end'
  | 'auth_start'
  | 'auth_end'
  | 'circuit_breaker'
  | 'deduplication';

/**
 * Timeline event for reconstructing request lifecycle.
 *
 * @example
 * ```typescript
 * const event: IntegrationTimelineEvent = {
 *   timestamp: dayjs().valueOf(),
 *   type: 'request_start',
 *   metadata: {
 *     method: 'GET',
 *     url: '/api/users',
 *   },
 * };
 * ```
 */
export interface IntegrationTimelineEvent {
  /** Timestamp of the event */
  timestamp: number;
  /** Type of timeline event */
  type: IntegrationTimelineEventType;
  /** Duration of the event (for end events) */
  duration?: number;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Network timing metrics from Performance API.
 *
 * @example
 * ```typescript
 * const networkMetrics: IntegrationNetworkMetrics = {
 *   dnsLookupTime: 10,
 *   connectTime: 50,
 *   tlsHandshakeTime: 30,
 *   firstByteTime: 100,
 *   downloadTime: 200,
 * };
 * ```
 */
export interface IntegrationNetworkMetrics {
  /** DNS lookup time in milliseconds */
  dnsLookupTime?: number;
  /** TCP connection time in milliseconds */
  connectTime?: number;
  /** TLS handshake time in milliseconds */
  tlsHandshakeTime?: number;
  /** Time to first byte in milliseconds */
  firstByteTime?: number;
  /** Download time in milliseconds */
  downloadTime?: number;
  /** Upload time in milliseconds */
  uploadTime?: number;
  /** Request headers sent */
  requestHeaders?: Record<string, string>;
  /** Response headers received */
  responseHeaders?: Record<string, string>;
}

/**
 * Snapshot of performance metrics at a point in time.
 *
 * @example
 * ```typescript
 * const snapshot: PerformanceSnapshot = {
 *   timestamp: dayjs().valueOf(),
 *   totalRequests: 1000,
 *   successfulRequests: 950,
 *   failedRequests: 50,
 *   retriedRequests: 100,
 *   averageResponseTime: 250,
 *   p50ResponseTime: 200,
 *   p95ResponseTime: 500,
 *   p99ResponseTime: 1000,
 *   circuitBreakerTrips: 2,
 *   authRefreshCount: 5,
 *   deduplicationSavings: 150,
 * };
 * ```
 */
export interface PerformanceSnapshot {
  /** Snapshot timestamp */
  timestamp: number;
  /** Total number of requests */
  totalRequests: number;
  /** Number of successful requests */
  successfulRequests: number;
  /** Number of failed requests */
  failedRequests: number;
  /** Number of retried requests */
  retriedRequests: number;
  /** Average response time in milliseconds */
  averageResponseTime: number;
  /** 50th percentile response time */
  p50ResponseTime: number;
  /** 95th percentile response time */
  p95ResponseTime: number;
  /** 99th percentile response time */
  p99ResponseTime: number;
  /** Number of circuit breaker trips */
  circuitBreakerTrips: number;
  /** Number of auth token refreshes */
  authRefreshCount: number;
  /** Number of requests saved by deduplication */
  deduplicationSavings: number;
}

// =============================================================================
// AUTH INTEGRATION INTERFACE
// =============================================================================

/**
 * Interface for the AuthIntegration class.
 *
 * Defines the contract for authentication integration components that handle
 * 401/403 errors. It holds no state of its own: whether a refresh is in flight
 * is the bearer lifecycle's to know.
 *
 * @example
 * ```typescript
 * const authIntegration: IAuthIntegration = createAuthIntegration(config);
 *
 * // Handle authentication error
 * const result = await authIntegration.handleAuthError(error);
 * ```
 */
export interface IAuthIntegration {
  /**
   * Handle authentication errors and coordinate with retry system.
   * A 401 is replayed if the bearer was rotated under the request, and ends
   * the session otherwise; a 403 is never retried.
   *
   * @param error - The authentication error to handle
   * @returns Promise resolving to the coordination result
   */
  handleAuthError(error: AuthErrorInterface): Promise<CoordinationResult>;

  /**
   * Determine if retry should be skipped for auth errors.
   * Returns true for 403 errors or skip endpoints.
   *
   * @param error - The authentication error to check
   * @returns True if retry should be skipped
   */
  shouldSkipRetry(error: AuthErrorInterface): boolean;

  /**
   * Update authentication configuration.
   *
   * @param config - Partial configuration to merge with existing config
   */
  updateConfig(config: Partial<AuthIntegrationConfig>): void;
}

// =============================================================================
// PERFORMANCE INTEGRATION INTERFACE
// =============================================================================

/**
 * Interface for the PerformanceIntegration class.
 *
 * Defines the contract for performance monitoring components that track
 * request metrics, retry attempts, circuit breaker events, and auth events.
 *
 * @example
 * ```typescript
 * const perfIntegration: IPerformanceIntegration = createPerformanceIntegration(config);
 *
 * // Track request lifecycle
 * perfIntegration.trackRequestStart(context);
 * perfIntegration.trackRequestEnd(context, response);
 *
 * // Get performance snapshot
 * const snapshot = perfIntegration.getPerformanceSnapshot();
 * console.log('Average response time:', snapshot.averageResponseTime);
 * ```
 */
export interface IPerformanceIntegration {
  /**
   * Track the start of a request.
   *
   * @param context - The request context containing request info
   */
  trackRequestStart(context: RequestContext): void;

  /**
   * Track the end of a request with optional response.
   *
   * @param context - The request context
   * @param response - Optional response object
   */
  trackRequestEnd(context: RequestContext, response?: unknown): void;

  /**
   * Track a retry attempt for a request.
   *
   * @param context - The request context
   * @param attempt - The retry attempt details
   */
  trackRetryAttempt(
    context: RequestContext,
    attempt: IntegrationRetryAttempt
  ): void;

  /**
   * Track a circuit breaker event.
   *
   * @param requestId - The request ID
   * @param state - The circuit breaker state
   * @param reason - The reason for the state change
   */
  trackCircuitBreakerEvent(
    requestId: string,
    state: IntegrationCircuitBreakerState,
    reason: string
  ): void;

  /**
   * Track an authentication event.
   *
   * @param requestId - The request ID
   * @param type - The type of auth event
   * @param duration - Optional duration of the operation
   * @param error - Optional error message
   */
  trackAuthEvent(
    requestId: string,
    type: IntegrationAuthEvent['type'],
    duration?: number,
    error?: string
  ): void;

  /**
   * Track a deduplication event.
   *
   * @param requestId - The request ID
   * @param wasDeduplicated - Whether the request was deduplicated
   * @param cacheKey - Optional cache key used
   * @param subscribers - Number of subscribers
   * @param cacheDuration - Duration the result was cached
   */
  trackDeduplication(
    requestId: string,
    wasDeduplicated: boolean,
    cacheKey?: string,
    subscribers?: number,
    cacheDuration?: number
  ): void;

  /**
   * Get performance data for a specific request.
   *
   * @param requestId - The request ID to retrieve metrics for
   * @returns Detailed metrics or null if not found
   */
  getPerformanceData(requestId: string): DetailedPerformanceMetrics | null;

  /**
   * Get all stored performance metrics.
   *
   * @returns Array of all performance metrics
   */
  getAllPerformanceData(): DetailedPerformanceMetrics[];

  /**
   * Get current performance snapshot with aggregate statistics.
   *
   * @returns Current performance snapshot
   */
  getPerformanceSnapshot(): PerformanceSnapshot;

  /**
   * Clear all stored performance data.
   */
  clearPerformanceData(): void;

  /**
   * Update performance integration configuration.
   *
   * @param config - Partial configuration to merge with existing config
   */
  updateConfig(config: Partial<PerformanceIntegrationConfig>): void;

  /**
   * Cleanup and destroy the performance integration.
   * Clears timers and stored data.
   */
  destroy(): void;
}

/**
 * Simplified retry attempt type for performance integration.
 * Used to avoid circular dependency with the full RetryAttempt type.
 */
export interface IntegrationRetryAttempt {
  /** Attempt number (1-based) */
  attemptNumber: number;
  /** Timestamp of the attempt */
  timestamp: number;
  /** Delay before this attempt in milliseconds */
  delay: number;
  /** Error that triggered the retry */
  error: {
    name: string;
    message: string;
  };
}

/**
 * Simplified circuit breaker state for performance integration.
 * Used to avoid circular dependency with the full CircuitBreakerState type.
 */
export interface IntegrationCircuitBreakerState {
  /** Current state (closed, open, half_open) */
  state: string;
  /** Affected endpoint */
  endpoint: string;
  /** Number of failures */
  failureCount: number;
}

/**
 * Simplified coordination result for auth integration.
 * Used to avoid circular dependency with the full CoordinationResult type.
 */
export interface CoordinationResult {
  /** Whether the operation was successful */
  success: boolean;
  /** Response data if successful */
  response?: unknown;
  /** Error if failed */
  error?: Error | AuthErrorInterface;
  /** Request context */
  context: RequestContext;
  /** Performance metrics */
  metrics: PerformanceMetrics;
}

// =============================================================================
// UNIFIED INTEGRATION TYPES
// =============================================================================

/**
 * Configuration for the unified integration that combines all integration layers.
 *
 * @example
 * ```typescript
 * const config: UnifiedIntegrationConfig = {
 *   axios: {
 *     enableLegacyMode: false,
 *     debug: true,
 *   },
 *   auth: {
 *     enableAutoRefresh: true,
 *   },
 *   performance: {
 *     enableDetailedMetrics: true,
 *     maxStoredRequests: 1000,
 *   },
 * };
 * ```
 */
export interface UnifiedIntegrationConfig {
  /** Axios integration configuration */
  axios?: Partial<AxiosIntegrationConfig>;
  /** Authentication integration configuration */
  auth?: Partial<AuthIntegrationConfig>;
  /** Performance integration configuration */
  performance?: Partial<PerformanceIntegrationConfig>;
}

/**
 * Interface for the unified integration combining all integration components.
 *
 * @example
 * ```typescript
 * const integration: UnifiedIntegration = {
 *   axios: axiosIntegration,
 *   auth: authIntegration,
 *   performance: performanceIntegration,
 *   setup: () => { ... },
 *   cleanup: () => { ... },
 *   getMetrics: () => ({ axios: {...}, performance: {...}, auth: {...} }),
 * };
 * ```
 */
export interface UnifiedIntegration {
  /** Axios integration instance */
  axios: IAxiosIntegration;
  /** Auth integration instance */
  auth: IAuthIntegration;
  /** Performance integration instance */
  performance: IPerformanceIntegration;
  /** Setup all integrations */
  setup(): void;
  /** Cleanup all integrations */
  cleanup(): void;
  /** Get metrics from all integrations */
  getMetrics(): {
    axios: AxiosIntegrationMetrics;
    performance: PerformanceSnapshot;
  };
}

// =============================================================================
// DEFAULT CONFIGURATIONS
// =============================================================================

/**
 * Default integration configuration.
 */
export const DEFAULT_INTEGRATION_CONFIG: IntegrationConfig = {
  preserveAuthInterceptor: true,
  preservePerformanceMonitoring: true,
  preserveNotificationSystem: true,
  enableLegacyCompatibility: false,
};

/**
 * Default coordinator configuration.
 */
export const DEFAULT_COORDINATOR_CONFIG: CoordinatorConfig = {
  retry: DEFAULT_RETRY_CONFIG,
  deduplication: DEFAULT_DEDUPLICATION_OPTIONS,
  enablePerformanceMonitoring: true,
  enableCircuitBreaker: true,
  enableNotifications: true,
  enableLogging: false, // Will be overridden by appEnv('development') at runtime
  memoryCleanupInterval: 300000, // 5 minutes
};
